import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import story1 from '../../images/image_story1.jpeg';
import story2 from '../../images/image_story2.jpeg';
import story3 from '../../images/image_story3.webp';
import story4 from '../../images/image_story4.webp';
import story5 from '../../images/image_story5.webp';
import story6 from '../../images/image_story6.jpg';

// Individual story point component to avoid hooks in map callback
const StoryPoint = ({ point, index, totalPoints }) => {
  const isLeft = index % 2 === 0;
  const elementRef = useRef(null);
  const textRef = useRef(null);
  const isInView = useInView(textRef, { once: false, amount: 0.3 });
  
  // Scroll-based animation for this specific story point
  const { scrollYProgress } = useScroll({
    target: elementRef,
    offset: ["start end", "end start"]
  });
  
  // Animation values based on scroll position
  const opacity = useTransform(scrollYProgress, [0.1, 0.5, 0.7, 0.9], [0, 1, 1, 0]);
  const yValue = useTransform(scrollYProgress, [0.1, 0.5, 0.7, 0.9], [50, 0, 0, 50]);
  
  // Parallax effect for images
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -30]);
  
  return (
    <div
      ref={elementRef}
      className={`relative max-w-[60rem] mx-auto ${
        isLeft ? "lg:ml-0 lg:mr-auto" : "lg:ml-auto lg:mr-0"
      }`}
    >
      {/* Visual Milestone Number */}
      <div className="absolute -left-4 lg:-left-12 top-1/2 transform -translate-y-1/2 hidden lg:block z-10">
        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-display text-h3 shadow-md">
          {index + 1}
        </div>
      </div>
      
      <motion.div
        style={{ 
          opacity: opacity,
          y: yValue
        }}
        className={`flex flex-col ${
          isLeft ? "lg:flex-row" : "lg:flex-row-reverse"
        } items-center gap-8 lg:gap-1`}
      >
        {/* Image with parallax effect */}
        <div className="w-full lg:w-3/4">
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-lg"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              style={{ y: parallaxY }} // Parallax scrolling effect
              className="h-full w-full"
            >
              <img
                src={point.imageUrl}
                alt={point.title}
                className="w-full h-72 object-cover"
              />
            </motion.div>
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-secondary/40" />
            <div className="absolute top-4 left-4 bg-cta text-white px-4 py-1 rounded-full text-bodyRegular font-bold">
              {point.year}
            </div>
          </motion.div>
        </div>

        {/* Text content with staggered reveal animation - REMOVED FIXED HEIGHT */}
        <div className="w-full lg:w-3/4" ref={textRef}>
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-sm relative"
          >
            {/* Staggered Content Reveal */}
            <motion.div
              className="w-12 h-1 bg-cta mb-4"
              initial={{ width: 0 }}
              animate={isInView ? { width: 48 } : { width: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
            
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-display text-h3 text-secondary mb-4"
            >
              {point.title}
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-bodyRegular text-textPrimary"
            >
              {point.description}
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Main component
const OurStory = () => {
  const containerRef = useRef(null);
  const [activeStoryPoint, setActiveStoryPoint] = useState(0);
  const storyPointRefs = useRef([]);
  
  // For parallax background effects
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  
  const backgroundY1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const backgroundY2 = useTransform(scrollYProgress, [0, 1], [0, -150]);

  // Story content - replace with your actual content
  const storyPoints = [
    {
      title: "Why It Started",
      description:
       "As a grandson of refugees and child of a psychiatrist specializing in refugee trauma, Students for Refugees was built to asssit the vulnerable population by tackling the global refugee crisis through Education, Action, and Advocacy. We equip students with knowledge, engage in direct aid efforts, and advocate for systemic change to uplift refugee communities.",
      imageUrl: story1
    },
    {
      title: "From Classroom Vision to Global Impact",
      description:
        "SFR began as a middle school IB project raising awareness about the refugee crisis. Transitioning to high school, we grew into a student-led club, and now stands as a 501(c)(3) nonprofit. SFR operates across four universities, six high schools in the U.S., and even internationally in Amman, Jordan and London, England.",
      imageUrl: story2
    },
    {
      title: "Expanding Our Reach",
      description:
        "Focused on awareness campaigns, fundraising, and volunteer work with refugee resettlement programs. Despite credibility hurdles as a student-led initiative, SFR forged alliances with established organizations and showcased real impact. Through collaboration, we expanded our network and fostered a growing community of passionate youth advocates.",
      imageUrl: story3
    },
    {
      title: "Empowering Through Education & Aid",
      description:
        "SFR supports refugees through education programs, direct aid, and advocacy. Collaborating with community leaders and nonprofits we address food insecurity, healthcare access, and economic empowerment. Our Refugee Education Program has provided thousands of tutoring hours to over 100 students, while aiding 6,000+ refugees facing food insecurity during COVID-19.",
      imageUrl: story4
    },
    {
      title: "Collaborations & Key Milestones",
      description:
        "We serve as health guides and interpreters at the annual Refugee Health Fair, benefiting 300+ refugees. Partnering with Shamba ya Amani, we empower refugee women farmers. SFR works with resettlement agencies, university programs, and global organizations, and is exploring alliances with UNHCR, plus a summer volunteer program for students abroad.",
      imageUrl: story5
    },
    {
      title: "Expanding Our Horizons",
      description:
        "SFR plans to grow its university and high school chapters, develop international service programs, and foster lasting NGO partnerships for sustained refugee support. Students can launch chapters, join service trips, or engage in advocacy. Donors and volunteers bolster our work through funding, mentorship, and hands-on involvement with refugee communities.",
      imageUrl: story6
    }
  ];

  // Create refs for each story point
  useEffect(() => {
    storyPointRefs.current = Array(storyPoints.length)
      .fill()
      .map((_, i) => storyPointRefs.current[i] || React.createRef());
  }, []);

  // Watch which story point is in view
  useEffect(() => {
    const observers = storyPointRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveStoryPoint(index);
          }
        },
        { threshold: 0.6 }
      );
      
      if (ref.current) {
        observer.observe(ref.current);
      }
      
      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (storyPointRefs.current[index]?.current) {
          observer.unobserve(storyPointRefs.current[index].current);
        }
      });
    };
  }, []);

  
  return (
    <section
      id = "our-story"
      ref={containerRef}
      className="relative bg-lightTeal overflow-hidden py-20"
    >
      {/* Subtle Background Elements with Parallax */}
      <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
        <motion.div 
          style={{ y: backgroundY1 }}
          className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary"
        />
        <motion.div 
          style={{ y: backgroundY2 }}
          className="absolute bottom-40 right-20 w-80 h-80 rounded-full bg-cta"
        />
        <motion.div 
          style={{ y: backgroundY1 }}
          className="absolute top-[40%] right-40 w-40 h-40 rounded-full bg-secondary"
        />
      </div>

      <div className="max-w-[90rem] mx-auto px-6 md:px-4 relative" style={{ zIndex: 1 }}>
        <div className="text-center mb-16 relative" style={{ zIndex: 2 }}>
          <h2 className="font-display text-h2 text-secondary mb-2">
            OUR STORY
          </h2>
          <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto">
            The journey of Students for Refugees from our founding to today
          </p>
          <div className="w-16 h-1 bg-cta mx-auto mt-4" />
        </div>

        {/* Story points with reduced vertical spacing for mobile and adaptive spacing for larger screens */}
        <div className="space-y-16 md:space-y-24 lg:space-y-32 mt-16 md:mt-24 lg:mt-32 relative">
          {storyPoints.map((point, index) => (
            <div ref={storyPointRefs.current[index]} key={index}>
              <StoryPoint 
                point={point} 
                index={index} 
                totalPoints={storyPoints.length}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurStory;